import { Component, Input, Output, EventEmitter, inject, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@design-system/lib/button/button.directive';
import { HierarchyNode, HierarchySidebarSelection } from '@app/core/models/data-dictionary.model';
import { ExportSelectionService } from '@app/core/services/export-selection.service';
import { IconComponent } from '@design-system/lib/icon/icon.component';
import { TooltipComponent } from '@design-system/lib/tooltip/tooltip.component';

@Component({
  selector: 'app-tree-node',
  standalone: true,
  imports: [FormsModule, ButtonDirective, IconComponent, TooltipComponent],
  templateUrl: './tree-node.component.html',
  styleUrl: './tree-node.component.scss',
})
export class TreeNodeComponent implements AfterViewChecked {
  exportSelectionService = inject(ExportSelectionService);
  @ViewChild('nodeCheckbox') nodeCheckboxRef?: ElementRef<HTMLInputElement>;
  private static activeDrag: { nodeId: number; nodeName: string; nodeType: HierarchyNode['type'] } | null = null;
  private isDragging = false;
  private justDragged = false;
  private dropPosition: 'inside' | null = null;
  private draggedNodeId: number | null = null;
  private draggedNodeName: string | null = null;
  
  @Input({ required: true }) node!: HierarchyNode;
  @Input() level = 0;
  @Input() isEditor = false;
  @Input() selectionTarget: HierarchySidebarSelection | null = null;
  @Input() showCheckboxes = false;
  @Input() selectedNodes = new Set<number>();

  @Output() nodeClick = new EventEmitter<HierarchyNode>();
  @Output() nodeToggle = new EventEmitter<HierarchyNode>();
  @Output() nodeEdit = new EventEmitter<HierarchyNode>();
  @Output() nodeDelete = new EventEmitter<HierarchyNode>();
  @Output() nodeSelect = new EventEmitter<{node: HierarchyNode, selected: boolean}>();
  @Output() nodeAddChild = new EventEmitter<HierarchyNode>();
  @Output() nodeMove = new EventEmitter<{
    nodeId: number;
    targetParentId: number | null;
    targetIndex: number;
    placement: 'inside';
    targetNodeId: number;
  }>();

  ngAfterViewChecked(): void {
    if (this.nodeCheckboxRef && this.node.type === 'category') {
      const el = this.nodeCheckboxRef.nativeElement;
      el.checked = this.isFolderChecked();
      el.indeterminate = this.isFolderIndeterminate();
    }
  }

  private getAllDictionaryIdsUnderNode(node: HierarchyNode): number[] {
    if (node.type === 'dictionary') return [node.id];
    return (node.children ?? []).flatMap(c => this.getAllDictionaryIdsUnderNode(c));
  }

  isFolderEmpty(): boolean {
    return this.node.type === 'category' && this.getAllDictionaryIdsUnderNode(this.node).length === 0;
  }

  isFolderChecked(): boolean {
    const ids = this.getAllDictionaryIdsUnderNode(this.node);
    return (
      ids.length > 0 &&
      ids.every(id => this.exportSelectionService.isSelected(id)) &&
      !this.hasEmptyDescendantCategory(this.node)
    );
  }

  isFolderIndeterminate(): boolean {
    if (this.isFolderChecked()) return false;
    const ids = this.getAllDictionaryIdsUnderNode(this.node);
    const count = ids.filter(id => this.exportSelectionService.isSelected(id)).length;
    return count > 0;
  }

  private hasEmptyDescendantCategory(node: HierarchyNode): boolean {
    for (const child of (node.children ?? [])) {
      if (child.type === 'category') {
        if (this.getAllDictionaryIdsUnderNode(child).length === 0) return true;
        if (this.hasEmptyDescendantCategory(child)) return true;
      }
    }
    return false;
  }

  isNodeSelected(nodeId: number): boolean {
    return this.exportSelectionService.isSelected(nodeId);
  }

  onNodeClick(): void {
    if (this.isDragging || this.justDragged) {
      return;
    }
    this.nodeClick.emit(this.node);
  }

  /** Stable @for track when numeric ids collide across node types */
  hierarchyRowKey(n: HierarchyNode): string {
    return `${n.type}-${n.id}`;
  }

  onToggle(event: Event): void {
    event.stopPropagation();
    this.nodeToggle.emit(this.node);
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    this.nodeEdit.emit(this.node);
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    this.nodeDelete.emit(this.node);
  }

  onCheckboxClick(event: Event): void {
    event.stopPropagation();
  }

  onCheckboxChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.nodeSelect.emit({ node: this.node, selected: checked });
  }

  onAddChild(event: Event): void {
    event.stopPropagation();
    this.nodeAddChild.emit(this.node);
  }

  onDragStart(event: DragEvent): void {
    if (!this.isEditor || this.node.type !== 'dictionary' || !event.dataTransfer) {
      return;
    }

    event.dataTransfer.setData('application/x-hierarchy-node-id', String(this.node.id));
    event.dataTransfer.setData('application/x-hierarchy-node-name', this.node.name);
    event.dataTransfer.setData('application/x-hierarchy-node-type', this.node.type);
    event.dataTransfer.effectAllowed = 'move';
    this.setCompactDragImage(event);
    TreeNodeComponent.activeDrag = {
      nodeId: this.node.id,
      nodeName: this.node.name,
      nodeType: this.node.type,
    };
    this.onDragStarted();
  }

  onDragOver(event: DragEvent): void {
    if (!this.isEditor) {
      return;
    }

    const draggedNodeType = this.getDraggedNodeType(event);
    if (draggedNodeType !== 'dictionary') {
      return;
    }

    const draggedNodeId = this.getDraggedNodeId(event);
    if (draggedNodeId == null || draggedNodeId === this.node.id) {
      this.clearDropState();
      return;
    }

    // Only allow dropping into folders (categories), not between table rows.
    if (this.node.type !== 'category') {
      this.clearDropState();
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    this.draggedNodeId = draggedNodeId;
    this.draggedNodeName = this.getDraggedNodeName(event);
    this.dropPosition = 'inside';
  }

  onDrop(event: DragEvent): void {
    if (!this.isEditor) {
      this.clearDropState();
      return;
    }

    event.preventDefault();
    const draggedNodeId = this.getDraggedNodeId(event);
    const draggedNodeType = this.getDraggedNodeType(event);
    if (!draggedNodeId || draggedNodeType !== 'dictionary') {
      this.clearDropState();
      return;
    }

    if (draggedNodeId === this.node.id) {
      this.clearDropState();
      return;
    }

    const placement = 'inside' as const;
    const movePayload = this.getDropTarget(draggedNodeId, placement);
    this.clearDropState();

    if (!movePayload) {
      return;
    }

    this.nodeMove.emit({
      nodeId: draggedNodeId,
      targetParentId: movePayload.targetParentId,
      targetIndex: movePayload.targetIndex,
      placement: movePayload.placement,
      targetNodeId: this.node.id,
    });
  }

  onDragLeave(event: DragEvent): void {
    const current = event.currentTarget as HTMLElement | null;
    const related = event.relatedTarget as Node | null;
    if (current && related && current.contains(related)) {
      return;
    }
    this.clearDropState();
  }

  onDragStarted(): void {
    this.isDragging = true;
  }

  onDragEnded(): void {
    this.isDragging = false;
    TreeNodeComponent.activeDrag = null;
    this.clearDropState();
    this.justDragged = true;
    setTimeout(() => {
      this.justDragged = false;
    }, 0);
  }

  isDropInside(): boolean {
    return this.dropPosition === 'inside';
  }

  // Backward-compatible helpers in case a stale template cache still references them.
  isDropBefore(): boolean {
    return false;
  }

  isDropAfter(): boolean {
    return false;
  }

  getDropPreviewText(): string {
    if (!this.draggedNodeName) {
      return '';
    }
    return `Move "${this.draggedNodeName}" to folder "${this.node.name}"`;
  }

  private getDropTarget(
    draggedNodeId: number,
    placement: 'inside',
  ): { targetParentId: number | null; targetIndex: number; placement: 'inside' } | null {
    if (this.node.type !== 'category') {
      return null;
    }

    // Prevent no-op glitches
    if (draggedNodeId === this.node.id) {
      return null;
    }

    return {
      targetParentId: this.node.id,
      targetIndex: this.node.children?.length ?? 0,
      placement,
    };
  }

  private clearDropState(): void {
    this.dropPosition = null;
    this.draggedNodeId = null;
    this.draggedNodeName = null;
  }

  private getDraggedNodeId(event: DragEvent): number | null {
    const fromDataTransfer = event.dataTransfer?.getData('application/x-hierarchy-node-id');
    const raw = fromDataTransfer || (TreeNodeComponent.activeDrag?.nodeId != null
      ? String(TreeNodeComponent.activeDrag.nodeId)
      : '');
    if (!raw) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private getDraggedNodeName(event: DragEvent): string | null {
    const fromDataTransfer = event.dataTransfer?.getData('application/x-hierarchy-node-name');
    return fromDataTransfer || TreeNodeComponent.activeDrag?.nodeName || null;
  }

  private getDraggedNodeType(event: DragEvent): HierarchyNode['type'] | null {
    const fromDataTransfer = event.dataTransfer?.getData('application/x-hierarchy-node-type') as HierarchyNode['type'] | '';
    return (fromDataTransfer || TreeNodeComponent.activeDrag?.nodeType || null) as HierarchyNode['type'] | null;
  }

  private setCompactDragImage(event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }

    const dragGhost = document.createElement('div');
    dragGhost.textContent = this.node.name;
    dragGhost.style.position = 'fixed';
    dragGhost.style.top = '-9999px';
    dragGhost.style.left = '-9999px';
    dragGhost.style.pointerEvents = 'none';
    dragGhost.style.padding = '2px 8px';
    dragGhost.style.maxWidth = '220px';
    dragGhost.style.whiteSpace = 'nowrap';
    dragGhost.style.overflow = 'hidden';
    dragGhost.style.textOverflow = 'ellipsis';
    dragGhost.style.fontSize = '11px';
    dragGhost.style.fontWeight = '600';
    dragGhost.style.color = '#ffffff';
    dragGhost.style.background = 'var(--primary)';
    dragGhost.style.border = '1px solid var(--primary)';
    dragGhost.style.borderRadius = '8px';
    dragGhost.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';

    document.body.appendChild(dragGhost);
    event.dataTransfer.setDragImage(dragGhost, 8, 8);

    setTimeout(() => {
      dragGhost.remove();
    }, 0);
  }
}
